/* eslint-disable camelcase */
import { Injectable } from '@angular/core';
import { UrlService } from '@datachain/ui-sdk/common';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Observable } from 'rxjs';

import { TagAdapter } from '../../core/adapters/tag.adapter';
import {
	DbTableNamingStrategy,
	ExportDbConfigurationEntity,
} from '../../export-template/components/export-db-configuration/export-db-configuration.entity';
import { ConnectorMode } from '../../export-template/components/export-template-details/export-connector.entity';
import { ExportTemplateDetailsEntity } from '../../export-template/components/export-template-details/export-template-details.entity';
import { WriteMode } from '../../export-template/components/export-write-mode-configuration/export-write-mode-configuration.entity';
import { AppMetadataEntity } from '../../ui/components/app-metadata/app-metadata.entity';
import { DcBaseRequester } from '../core/dc-base.requester';
import { IExportTemplateResponse } from './models/export-template.models';

/* eslint-disable @typescript-eslint/no-explicit-any */
type IExportDatabaseTemplate = any;

@Injectable({
	providedIn: 'root',
})
export class ExportDatabaseTemplateRequester extends DcBaseRequester<
	IExportDatabaseTemplate,
	{ templateId: string },
	{ project: number }
> {
	protected resource;
	protected elementsListUrl;
	protected singleElementUrl;
	public constructor(
		private readonly urlService: UrlService,
		private readonly tagPayloadAdapter: TagAdapter
	) {
		super();
		this.resource = this.generateResourceUrl('DATABLOCK_EXPORT_TEMPLATE');
		this.elementsListUrl = `${this.resource}`;
		this.singleElementUrl = `${this.elementsListUrl}/{templateId}`;
	}

	public updateTemplate(
		isNew: boolean,
		payload: {
			id?: number;
			templateDetails: ExportTemplateDetailsEntity;
			templateMetadata: AppMetadataEntity;
			templateTags: Array<AppTagItemEntity>;
		}
	): Observable<IExportTemplateResponse> {
		const body: IExportTemplateResponse = {
			metadata: {
				id: payload.templateMetadata.id, // FIXME: not necessary if metadata is handled on server side
				label: payload.templateMetadata.title,
				description: payload.templateMetadata.description,
				actif: payload.templateMetadata.isActive,
				tags: this.tagPayloadAdapter.generateMetadataTagPayload(
					payload.templateTags
				),
			},
			config: this.generatePayload(payload.templateDetails),
		};
		let url: string;
		if (!isNew && payload.id !== undefined) {
			url = this.urlService.generateUrl(
				this.generateResourceUrl('DATABLOCK_EXPORT_TEMPLATE_UPDATE'),
				{
					templateId: payload.id,
				},
				{
					project: this.projectId,
				}
			);
			return this.http.put<IExportTemplateResponse>(url, body);
		}

		url = this.urlService.generateUrl(
			this.generateResourceUrl('DATABLOCK_EXPORT_TEMPLATE_POST'),
			{},
			{
				project: this.projectId,
			}
		);
		return this.http.post<IExportTemplateResponse>(url, body);
	}

	public getById(id: number): Observable<IExportTemplateResponse> {
		const url = this.urlService.generateUrl(
			this.singleElementUrl,
			{
				templateId: id,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.get<IExportTemplateResponse>(url);
	}

	public generatePayload(
		payload: ExportTemplateDetailsEntity
	): { type: string } & Record<string, boolean | string | number | undefined> {
		const config = payload.config as ExportDbConfigurationEntity;
		if (
			payload.connector.type === ConnectorMode.SQL ||
			payload.connector.type === ConnectorMode.NoSQL
		) {
			return {
				type: 'DATABASE',
				connector_id: payload.connector.id,
				keep_original_labels: payload.keepOriginalLabels,
				compress: payload.compress,
				path: payload.path,
				key: config.targetLabel,
				naming_strategy: this.selectNamingStrategy(config.namingStrategy),
				mode: this.selectWriteMode(payload.writeMode.mode),
				schema: config.schema,
			};
		} else if (payload.connector.type === ConnectorMode.Neo4j) {
			return {
				type: 'NEO4J',
				connector_id: payload.connector.id,
				keep_original_labels: payload.keepOriginalLabels,
				compress: payload.compress,
				path: payload.path,
				key: config.targetLabel,
				node_keys: config.targetKey,
				naming_strategy: this.selectNamingStrategy(config.namingStrategy),
				mode: this.selectWriteMode(payload.writeMode.mode),
			};
		}
		throw new Error('Could not prepare database export template payload');
	}

	private selectWriteMode(mode: WriteMode): string {
		if (mode === WriteMode.Append) {
			return 'APPEND';
		}
		if (mode === WriteMode.Overwrite) {
			return 'OVERWRITE';
		}
		if (mode === WriteMode.Truncate) {
			return 'OVERWRITE_TRUNCATE';
		}
		return 'NONE';
	}

	private selectNamingStrategy(strategy: DbTableNamingStrategy): string {
		if (strategy === DbTableNamingStrategy.SameAsDatablock) {
			return 'SAME_AS_DATABLOCK';
		}
		if (strategy === DbTableNamingStrategy.UserDefinedLabel) {
			return 'USER_DEFINED_LABEL';
		}
		return '';
	}
}
